import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@cwos/ui-admin';
import { directory } from '@cwos/api-client';
import { usePermissions } from '../permissions.js';
import { authorityGate, gateOpen, gateRefused } from '../authorityGate.js';
import { GateNotice } from '../GateNotice.js';
import { LoadFailure } from '../LoadFailure.js';
import { listPhase } from '../listState.js';
import { pickerState } from '../pickerState.js';
import { insights, type FindingDetail, type FindingOwnerAssignment } from './insightsClient.js';
import {
  UNDATED_SUGGESTION_NOTE,
  consultedSentence,
  ownerSentence,
  suggestionSentence,
} from './ownerLogic.js';

/**
 * WHO OWNS THIS FINDING, AND THE ACT OF DECIDING.
 *
 * ===========================================================================
 * THREE STATES ON THE SCREEN, NOT TWO
 * ===========================================================================
 * The line this panel replaces was `finding.ownerUserId ? 'Assigned' :
 * 'Nobody has taken this on'`. Everything here exists so that a role-owned
 * finding never reads as ownerless and an undecided one never reads as
 * deliberately unowned. `ownerSentence` is where each of the three (and the
 * three disclosures inside the person state) gets its own words.
 *
 * ===========================================================================
 * THE SUGGESTION IS VISIBLY A SUGGESTION
 * ===========================================================================
 * Three things keep it one, and none of them is the wording alone:
 *
 *   - It sits under its own heading, headed "What your records say", and every
 *     sentence names the RELATION it came from. Nothing says "Phibian thinks".
 *   - Accepting it does not submit. It fills the control in, and the person
 *     still presses the button — so the act on the wire is always a person
 *     naming an owner, never a suggestion redeeming itself. The server agrees:
 *     the POST carries the owner explicitly and there is no "accept" verb.
 *   - The acceptance is RECORDED as an acceptance
 *     (`acceptedSuggestionFrom`), so the history says a person took the
 *     organization's own record rather than deciding from nothing.
 *
 * ===========================================================================
 * AND THE HISTORY CANNOT BE REWRITTEN, SO NOTHING HERE OFFERS TO
 * ===========================================================================
 * `finding_event` holds no UPDATE and no DELETE for the application role. There
 * is no edit control and no remove control on a history entry, deliberately:
 * an affordance implying one would be a promise the database refuses to keep.
 * Reassignment adds an entry; it never replaces one.
 *
 * ===========================================================================
 * FOUR NOT-OPEN ANSWERS, AND ONLY ONE OF THEM MAY ACCUSE
 * ===========================================================================
 * The write control is gated with `authorityGate` + `GateNotice`, never a bare
 * `if (!has(...))`. `usePermissions()` resolves an in-flight or failed
 * `/auth/me` to an empty set, so a screen that refused off `has()` alone would
 * tell somebody they may not assign an owner while it was still finding out —
 * which is the defect
 * `docs/intelligence/audit/UNKNOWN-IS-NOT-DENIED-FRONTEND.md` closed across
 * eleven sites.
 */

const INSIGHTS_TRIAGE_KEY = 'insights:triage';
const DIRECTORY_READ_KEY = 'directory:read';

type Choice =
  | { kind: 'none' }
  | { kind: 'user'; userId: string }
  | { kind: 'role'; roleId: string }
  | { kind: 'unresolved' };

export function OwnerPanel({ finding }: { readonly finding: FindingDetail }) {
  const permissions = usePermissions();
  const queryClient = useQueryClient();
  const gate = authorityGate(permissions, INSIGHTS_TRIAGE_KEY);
  const mayAssign = gateOpen(gate);
  const mayReadDirectory = gateOpen(authorityGate(permissions, DIRECTORY_READ_KEY));

  const [choice, setChoice] = useState<Choice>({ kind: 'none' });
  const [acceptedFrom, setAcceptedFrom] = useState<'program_lead' | 'role_holder' | null>(null);

  // The suggestion read charges the READ key, so it runs for a reader who may
  // not assign: seeing who the organization's records say is responsible is
  // reading, and hiding it from them would withhold a fact they may have.
  const suggestions = useQuery({
    queryKey: ['insights', 'finding', finding.id, 'owner-suggestions'],
    queryFn: () => insights.ownerSuggestions(finding.id),
  });

  const workers = useQuery({
    queryKey: ['directory', 'workers', 'for-finding-owner'],
    queryFn: () => directory.workers(),
    // A default-deny `enabled:` flag is the SAFE direction and the audit names
    // it as correct: it hides a control while the answer is unknown and says
    // nothing false to anybody.
    enabled: mayAssign && mayReadDirectory,
  });

  const assign = useMutation({
    mutationFn: (next: FindingOwnerAssignment) =>
      insights.assignOwner(finding.id, next, { acceptedSuggestionFrom: acceptedFrom }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['insights', 'finding', finding.id], updated);
      void queryClient.invalidateQueries({ queryKey: ['insights', 'findings'] });
      void queryClient.invalidateQueries({
        queryKey: ['insights', 'finding', finding.id, 'owner-suggestions'],
      });
      setChoice({ kind: 'none' });
      setAcceptedFrom(null);
    },
  });

  const current = ownerSentence(finding.owner);
  const people = (workers.data ?? []).filter((worker) => worker.userId);
  const picker = pickerState(
    mayReadDirectory ? listPhase(workers, people.length) : { kind: 'refused' },
    'people',
    'Choose someone…',
  );
  const roleOptions = (suggestions.data?.suggestions ?? []).flatMap((entry) =>
    entry.source === 'role_holder'
      ? [{ roleId: entry.owner.roleId, name: entry.roleName, holders: entry.resolvesToday }]
      : [],
  );

  const submit = (): void => {
    if (choice.kind === 'none') return;
    assign.mutate(
      choice.kind === 'unresolved'
        ? { kind: 'unresolved' }
        : choice.kind === 'user'
          ? { kind: 'user', userId: choice.userId }
          : { kind: 'role', roleId: choice.roleId },
    );
  };

  return (
    <section className="rn-card" data-owner-panel="finding">
      <h2 className="rn-card-title">Owner</h2>

      <p data-owner-state={finding.owner.kind} data-owner-tone={current.tone}>
        <span className="rn-strong">{current.headline}</span>
        {current.detail ? <span className="rn-muted"> {current.detail}</span> : null}
      </p>

      {/* ---- What the organization's own records say --------------------- */}
      <div data-owner-suggestions>
        <h3 className="rn-card-title">What your records say</h3>
        {suggestions.isLoading ? (
          <p className="rn-muted" role="status">
            Looking up who your records say is responsible…
          </p>
        ) : suggestions.isError ? (
          <LoadFailure
            error={suggestions.error}
            message="We could not read who your records say is responsible. Nothing has changed."
            onRetry={() => void suggestions.refetch()}
            refusedHeading="You cannot see who your records say is responsible"
          />
        ) : (
          <>
            <p className="rn-muted">{UNDATED_SUGGESTION_NOTE}</p>
            {suggestions.data && suggestions.data.suggestions.length > 0 ? (
              <ul className="rn-list">
                {suggestions.data.suggestions.map((entry) => (
                  <li key={`${entry.source}:${JSON.stringify(entry.owner)}`}>
                    <span>{suggestionSentence(entry)}</span>{' '}
                    {mayAssign ? (
                      <button
                        type="button"
                        className="rn-btn-quiet"
                        onClick={() => {
                          setChoice(
                            entry.owner.kind === 'user'
                              ? { kind: 'user', userId: entry.owner.userId }
                              : { kind: 'role', roleId: entry.owner.roleId },
                          );
                          setAcceptedFrom(entry.source);
                        }}
                      >
                        Use this
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}
            {/* An empty list is not silence: say what was consulted. */}
            <ul className="rn-list" data-owner-consulted>
              {(suggestions.data?.consulted ?? []).map((note) => {
                const sentence = consultedSentence(note);
                return sentence === null ? null : (
                  <li key={note.source} className="rn-muted">
                    {sentence}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      {/* ---- Deciding, which is a person's act --------------------------- */}
      <GateNotice
        gate={gate}
        what="decide who owns this finding"
        heading="Deciding who owns this is not yours to do"
        detail="Recording an owner needs the Insights triage permission. You can still see who your records say is responsible."
        onRetry={() => permissions.refetch()}
      />

      {mayAssign ? (
        <div className="rn-col" data-owner-assign>
          <h3 className="rn-card-title">Record who owns this</h3>
          {picker.note ? <p className="rn-muted">{picker.note}</p> : null}
          <label>
            A person
            <select
              value={choice.kind === 'user' ? choice.userId : ''}
              disabled={picker.disabled}
              onChange={(event) => {
                const value = event.target.value;
                setChoice(value === '' ? { kind: 'none' } : { kind: 'user', userId: value });
                // Typing over a suggestion is no longer accepting it. The
                // history must not claim they took the organization's record
                // when they went and picked somebody else.
                setAcceptedFrom(null);
              }}
            >
              <option value="">{picker.placeholder}</option>
              {people.map((worker) => (
                <option key={worker.userId!} value={worker.userId!}>
                  {worker.displayName}
                </option>
              ))}
            </select>
          </label>

          <label>
            Or a responsible role
            <select
              value={choice.kind === 'role' ? choice.roleId : ''}
              disabled={roleOptions.length === 0}
              onChange={(event) => {
                const value = event.target.value;
                setChoice(value === '' ? { kind: 'none' } : { kind: 'role', roleId: value });
                setAcceptedFrom(null);
              }}
            >
              <option value="">
                {roleOptions.length === 0
                  ? 'No role here currently has a holder who covers this finding'
                  : 'Choose a role…'}
              </option>
              {roleOptions.map((role) => (
                <option key={role.roleId} value={role.roleId}>
                  {role.name} — {role.holders === 1 ? '1 holder' : `${role.holders} holders`}
                </option>
              ))}
            </select>
          </label>

          {finding.owner.kind !== 'unresolved' ? (
            <label>
              <input
                type="checkbox"
                checked={choice.kind === 'unresolved'}
                onChange={(event) => {
                  setChoice(event.target.checked ? { kind: 'unresolved' } : { kind: 'none' });
                  setAcceptedFrom(null);
                }}
              />{' '}
              Put this back to undecided
              <span className="rn-muted">
                {' '}
                — the decisions already recorded stay in the history.
              </span>
            </label>
          ) : null}

          <Button
            onClick={submit}
            disabled={choice.kind === 'none' || assign.isPending}
            variant="secondary"
          >
            Record owner
          </Button>

          {assign.isError ? (
            <LoadFailure
              error={assign.error}
              message="We could not record that. Nothing has changed."
              refusedHeading="You cannot make that person accountable"
            />
          ) : null}
        </div>
      ) : null}

      {/* ---- The append-only record. No edit, no delete. ----------------- */}
      <div data-owner-history>
        <h3 className="rn-card-title">Ownership decisions</h3>
        {finding.ownerHistory.length === 0 ? (
          <p className="rn-muted">
            Nobody has recorded an owner for this finding. That is why it reads as undecided rather
            than as unowned.
          </p>
        ) : (
          <ul className="rn-list">
            {finding.ownerHistory.map((entry) => (
              <li key={entry.eventId}>
                <span className="rn-strong">{ownerSentence(entry.to).headline}</span>{' '}
                <span className="rn-muted">
                  <time dateTime={entry.occurredAt}>
                    {new Date(entry.occurredAt).toLocaleString()}
                  </time>
                  {entry.acceptedSuggestionFrom === null
                    ? ' · decided by a person'
                    : ' · a person accepted what your records said'}
                </span>
                {entry.reason ? <p>{entry.reason}</p> : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* The one sentence that stops somebody looking for an edit button. */}
      {gateRefused(gate) ? null : (
        <p className="rn-muted">
          Every decision above is permanent. Reassigning adds a decision; it does not replace one.
        </p>
      )}
    </section>
  );
}
